/**
 * @module components/cases/ConvertCaseToClaimModal
 * @description Modal zum Konvertieren eines Cases in eine Klärungsanfrage.
 *   AI-Vorschlag (Haiku 4.5) manuell triggerbar, 4 editierbare Felder, Customer-Autocomplete-Preselect.
 *
 * #1240 — Case→Klärung-Konvertierung.
 *
 * @since v2.536.0
 */
import { useEffect, useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { X, Sparkles, AlertTriangle, Loader2 } from 'lucide-react';
import CustomerAutocompleteSearch from '../CustomerAutocompleteSearch';

const API = import.meta.env.VITE_API_URL || '';

export default function ConvertCaseToClaimModal({ isOpen, caseTask, onClose, onSuccess }) {
  const { t } = useLanguage();
  const [customerId, setCustomerId] = useState('');
  const [customerLabel, setCustomerLabel] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [description, setDescription] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [confidence, setConfidence] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (!isOpen || !caseTask) return;
    setCustomerId(caseTask.company_id_num ? String(caseTask.company_id_num) : '');
    setCustomerLabel(caseTask.company_name || caseTask.partner_name || '');
    setTrackingNumber('');
    setOrderNumber('');
    setDescription('');
    setAiError(null);
    setConfidence(null);
    setSubmitError(null);
  }, [isOpen, caseTask]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose?.(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  const handleAiExtract = async () => {
    if (!caseTask?.id) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const res = await fetch(`${API}/api/cases/${caseTask.id}/ai-extract-for-clarification`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      if (data.tracking_number) setTrackingNumber(data.tracking_number);
      if (data.order_number) setOrderNumber(data.order_number);
      if (data.description) setDescription(data.description);
      setConfidence(typeof data.confidence === 'number' ? data.confidence : null);
    } catch (e) {
      setAiError(e.message || 'AI-Extraction fehlgeschlagen');
    } finally {
      setAiLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!customerId || !trackingNumber.trim() || !description.trim()) {
      setSubmitError(t('cases.convert.missingRequired') || 'Bitte Kunde, Tracking-Nr und Beschreibung ausfüllen.');
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch(`${API}/api/cases/${caseTask.id}/convert-to-clarification`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: Number(customerId),
          tracking_number: trackingNumber.trim(),
          order_number: orderNumber.trim() || null,
          description: description.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      onSuccess?.(data);
    } catch (e) {
      setSubmitError(e.message || 'Konvertierung fehlgeschlagen');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const lowConfidence = confidence !== null && confidence < 0.5;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="w-full max-w-[640px] mx-4 rounded-[16px] bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-card,#E5E7EB)]">
          <h2 className="text-[16px] font-semibold text-[var(--text-primary,#1A1A2E)]">
            {t('cases.convert.title') || 'Case in Klärung konvertieren'}
          </h2>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-[var(--bg-inset,#F8F9FC)]">
            <X className="w-4 h-4 text-[var(--text-tertiary,#6B7280)]" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <p className="text-[13px] text-[var(--text-tertiary,#6B7280)]">
            {t('cases.convert.subtitle') || 'Erstellt eine neue Klärungsanfrage aus diesem Case. Anhänge werden kopiert, der Case wird geschlossen.'}
          </p>

          <div>
            <button
              type="button"
              onClick={handleAiExtract}
              disabled={aiLoading}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-[8px] text-[13px] font-medium border border-[#338edc] text-[#338edc] hover:bg-[#338edc]/5 disabled:opacity-50"
            >
              {aiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {t('cases.convert.aiButton') || 'AI-Vorschlag laden'}
            </button>
            {aiError && (
              <div className="mt-2 text-[12px] text-[#DC2626]">{aiError}</div>
            )}
            {lowConfidence && (
              <div className="mt-2 flex items-start gap-2 px-3 py-2 rounded-[8px] bg-[#FEF3C7] text-[12px] text-[#92400E]">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>{t('cases.convert.lowConfidence') || `Niedrige AI-Confidence (${Math.round(confidence * 100)}%) — Vorschlag bitte prüfen.`}</span>
              </div>
            )}
          </div>

          <div>
            <label className="block text-[12px] font-medium text-[var(--text-secondary,#4B5563)] mb-1">
              {t('cases.convert.customer') || 'Kunde'} <span className="text-[#DC2626]">*</span>
            </label>
            <CustomerAutocompleteSearch
              value={customerId}
              selectedLabel={customerLabel}
              onChange={(id, k) => { setCustomerId(id); setCustomerLabel(k?.name || ''); }}
              placeholder={t('cases.convert.customerPlaceholder') || 'Kunde suchen…'}
            />
          </div>

          <div>
            <label className="block text-[12px] font-medium text-[var(--text-secondary,#4B5563)] mb-1">
              {t('cases.convert.trackingNumber') || 'Tracking-Nr'} <span className="text-[#DC2626]">*</span>
            </label>
            <input
              type="text"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              className="w-full px-3 py-2 text-[14px] rounded-[8px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]"
              placeholder="AT1234567890"
            />
          </div>

          <div>
            <label className="block text-[12px] font-medium text-[var(--text-secondary,#4B5563)] mb-1">
              {t('cases.convert.orderNumber') || 'Bestell-Nr (optional)'}
            </label>
            <input
              type="text"
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
              className="w-full px-3 py-2 text-[14px] rounded-[8px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]"
              placeholder="ORD-12345"
            />
          </div>

          <div>
            <label className="block text-[12px] font-medium text-[var(--text-secondary,#4B5563)] mb-1">
              {t('cases.convert.description') || 'Beschreibung'} <span className="text-[#DC2626]">*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 text-[14px] rounded-[8px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)] resize-vertical"
              placeholder={t('cases.convert.descriptionPlaceholder') || 'Was soll geklärt werden?'}
            />
          </div>

          {submitError && (
            <div className="px-3 py-2 rounded-[8px] bg-[#FEE2E2] text-[12px] text-[#991B1B]">{submitError}</div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 rounded-[8px] text-[13px] font-medium border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-inset,#F8F9FC)] disabled:opacity-50"
            >
              {t('common.cancel') || 'Abbrechen'}
            </button>
            <button
              type="submit"
              disabled={submitting || !customerId || !trackingNumber.trim() || !description.trim()}
              className="px-4 py-2 rounded-[8px] text-[13px] font-medium bg-[#338edc] text-white hover:bg-[#2477c5] disabled:opacity-50 inline-flex items-center gap-2"
            >
              {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {t('cases.convert.submit') || 'Klärung erstellen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
